import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Upload, Save, RotateCcw } from 'lucide-react';
import AppHeader from '../components/AppHeader';

const MockupDesigner = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('front');
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Store logos per view
  const [viewLogos, setViewLogos] = useState({
    front: { image: null, file: null, position: { x: 150, y: 200 }, size: 150 },
    back: { image: null, file: null, position: { x: 150, y: 200 }, size: 150 },
    left_sleeve: { image: null, file: null, position: { x: 150, y: 250 }, size: 100 },
    right_sleeve: { image: null, file: null, position: { x: 150, y: 250 }, size: 100 }
  });

  // T-shirt templates (using SVG for better quality)
  const shirtTemplates = {
    front: `
      <svg width="400" height="500" viewBox="0 0 400 500" xmlns="http://www.w3.org/2000/svg">
        <!-- Body -->
        <path d="M 100 100 L 100 450 L 300 450 L 300 100 Z" fill="currentColor" stroke="#333" stroke-width="2"/>
        <!-- Left Sleeve -->
        <path d="M 100 100 L 50 150 L 50 200 L 100 180 Z" fill="currentColor" stroke="#333" stroke-width="2"/>
        <!-- Right Sleeve -->
        <path d="M 300 100 L 350 150 L 350 200 L 300 180 Z" fill="currentColor" stroke="#333" stroke-width="2"/>
        <!-- Collar -->
        <ellipse cx="200" cy="80" rx="30" ry="20" fill="currentColor" stroke="#333" stroke-width="2"/>
      </svg>
    `,
    back: `
      <svg width="400" height="500" viewBox="0 0 400 500" xmlns="http://www.w3.org/2000/svg">
        <!-- Body -->
        <path d="M 100 100 L 100 450 L 300 450 L 300 100 Z" fill="currentColor" stroke="#333" stroke-width="2"/>
        <!-- Left Sleeve -->
        <path d="M 100 100 L 50 150 L 50 200 L 100 180 Z" fill="currentColor" stroke="#333" stroke-width="2"/>
        <!-- Right Sleeve -->
        <path d="M 300 100 L 350 150 L 350 200 L 300 180 Z" fill="currentColor" stroke="#333" stroke-width="2"/>
      </svg>
    `,
    left_sleeve: `
      <svg width="400" height="500" viewBox="0 0 400 500" xmlns="http://www.w3.org/2000/svg">
        <path d="M 150 100 L 100 150 L 100 350 L 150 380 L 250 380 L 300 350 L 300 150 L 250 100 Z" 
              fill="currentColor" stroke="#333" stroke-width="2"/>
      </svg>
    `,
    right_sleeve: `
      <svg width="400" height="500" viewBox="0 0 400 500" xmlns="http://www.w3.org/2000/svg">
        <path d="M 150 100 L 100 150 L 100 350 L 150 380 L 250 380 L 300 350 L 300 150 L 250 100 Z" 
              fill="currentColor" stroke="#333" stroke-width="2"/>
      </svg>
    `
  };

  useEffect(() => {
    fetchProposal();
  }, [id]);

  useEffect(() => {
    if (proposal) {
      drawCanvas();
    }
  }, [proposal, activeView, viewLogos, isDragging]);

  const fetchProposal = async () => {
    try {
      const response = await axios.get(`/proposals/${id}`);
      setProposal(response.data);
      
      // Load existing mockup views if available
      if (response.data.mockup_views) {
        const updatedLogos = { ...viewLogos };
        Object.keys(response.data.mockup_views).forEach(view => {
          if (updatedLogos[view]) {
            updatedLogos[view].image = `data:image/png;base64,${response.data.mockup_views[view]}`;
          }
        });
        setViewLogos(updatedLogos);
      }
    } catch (error) {
      toast.error('Failed to load proposal');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    console.log('File selected:', file);
    
    if (!file) {
      console.log('No file selected');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const imageData = event.target.result;
        console.log('Logo loaded for', activeView, 'Data URL length:', imageData.length);
        
        setViewLogos(prev => {
          const updated = {
            ...prev,
            [activeView]: {
              ...prev[activeView],
              image: imageData,
              file: file
            }
          };
          console.log('Updated viewLogos:', updated);
          return updated;
        });
        
        toast.success(`Logo uploaded for ${activeView.replace('_', ' ').toUpperCase()}!`);
      } catch (error) {
        console.error('Error processing image:', error);
        toast.error('Failed to process image');
      }
    };
    
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      toast.error('Failed to read file. Please try again.');
    };
    
    reader.onabort = () => {
      console.error('FileReader aborted');
      toast.error('File read was cancelled');
    };
    
    try {
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error starting file read:', error);
      toast.error('Failed to read file');
    }
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !proposal) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    console.log('Drawing canvas for view:', activeView, 'Logo:', viewLogos[activeView]?.image ? 'present' : 'none');

    // Set background
    ctx.fillStyle = '#F7FAFC';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Get shirt color
    const shirtColor = proposal.items && proposal.items[0] ? proposal.items[0].color : 'White';
    const colorMap = {
      'Black': '#000000',
      'White': '#FFFFFF',
      'Navy': '#001F3F',
      'Red': '#FF4136',
      'Royal Blue': '#0074D9',
      'Forest Green': '#2ECC40',
      'Maroon': '#85144B',
      'Carolina Blue': '#7FDBFF',
      'Gray': '#AAAAAA',
      'Heather Gray': '#B0B0B0',
      'Charcoal': '#36454F'
    };

    const shirtHex = colorMap[shirtColor] || '#FFFFFF';

    // Draw t-shirt shape
    ctx.fillStyle = shirtHex;
    ctx.strokeStyle = '#333333';
    ctx.lineWidth = 2;

    if (activeView === 'front' || activeView === 'back') {
      // Body
      ctx.fillRect(100, 150, 200, 300);
      ctx.strokeRect(100, 150, 200, 300);

      // Left sleeve
      ctx.beginPath();
      ctx.moveTo(100, 150);
      ctx.lineTo(50, 200);
      ctx.lineTo(50, 250);
      ctx.lineTo(100, 230);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // Right sleeve
      ctx.beginPath();
      ctx.moveTo(300, 150);
      ctx.lineTo(350, 200);
      ctx.lineTo(350, 250);
      ctx.lineTo(300, 230);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // Collar (only on front)
      if (activeView === 'front') {
        ctx.beginPath();
        ctx.ellipse(200, 130, 25, 15, 0, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
      }
    } else if (activeView === 'left_sleeve' || activeView === 'right_sleeve') {
      // Sleeve view
      ctx.beginPath();
      ctx.moveTo(150, 150);
      ctx.lineTo(100, 180);
      ctx.lineTo(100, 380);
      ctx.lineTo(150, 400);
      ctx.lineTo(250, 400);
      ctx.lineTo(300, 380);
      ctx.lineTo(300, 180);
      ctx.lineTo(250, 150);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    // Add view label
    ctx.fillStyle = '#4A5568';
    ctx.font = '14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(activeView.replace('_', ' ').toUpperCase(), 200, 480);
    
    // Draw logo for current view if available
    const currentLogo = viewLogos[activeView];
    if (currentLogo && currentLogo.image) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        // Redraw the entire canvas with the logo
        ctx.save();
        ctx.globalAlpha = 0.9;
        ctx.drawImage(img, currentLogo.position.x, currentLogo.position.y, currentLogo.size, currentLogo.size);
        ctx.restore();

        // Draw selection border if not dragging
        if (!isDragging) {
          ctx.strokeStyle = '#667eea';
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 5]);
          ctx.strokeRect(currentLogo.position.x, currentLogo.position.y, currentLogo.size, currentLogo.size);
          ctx.setLineDash([]);
        }
      };
      img.onerror = (e) => {
        console.error('Failed to load logo image:', e);
      };
      img.src = currentLogo.image;
    }
  };

  const handleMouseDown = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const currentLogo = viewLogos[activeView];
    
    // Check if clicking on logo
    if (
      currentLogo && currentLogo.image &&
      x >= currentLogo.position.x &&
      x <= currentLogo.position.x + currentLogo.size &&
      y >= currentLogo.position.y &&
      y <= currentLogo.position.y + currentLogo.size
    ) {
      setIsDragging(true);
      setDragOffset({
        x: x - currentLogo.position.x,
        y: y - currentLogo.position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const currentLogo = viewLogos[activeView];
    
    setViewLogos(prev => ({
      ...prev,
      [activeView]: {
        ...prev[activeView],
        position: {
          x: Math.max(0, Math.min(x - dragOffset.x, canvas.width - currentLogo.size)),
          y: Math.max(0, Math.min(y - dragOffset.y, canvas.height - currentLogo.size))
        }
      }
    }));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleSaveMockup = async () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      toast.error('Canvas not ready');
      return;
    }

    try {
      const mockupBase64 = canvas.toDataURL('image/png').split(',')[1];

      // Get existing mockup views
      const mockupViews = { ...(proposal.mockup_views || {}) };
      mockupViews[activeView] = mockupBase64;

      // Save mockup views to proposal
      await axios.put(`/proposals/${id}/mockup-views`, mockupViews);

      // Update local state
      setProposal(prev => ({ ...prev, mockup_views: mockupViews }));

      // Save individual logo if uploaded
      const currentLogo = viewLogos[activeView];
      if (currentLogo && currentLogo.file) {
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            if (reader.result) {
              const logoBase64 = reader.result.split(',')[1];
              const formData = new FormData();
              formData.append('logo_base64', logoBase64);
              await axios.put(`/proposals/${id}/logo`, formData);
            }
          } catch (error) {
            console.error('Error saving logo:', error);
          }
        };
        reader.onerror = () => {
          console.error('Failed to read logo file');
        };
        reader.readAsDataURL(currentLogo.file);
      }

      toast.success(`${activeView.replace('_', ' ').toUpperCase()} mockup saved successfully!`);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save mockup');
    }
  };

  const handleSaveAll = async () => {
    try {
      const savedMockups = { ...(proposal.mockup_views || {}) };
      const currentView = activeView;
      
      // Save current view first
      const canvas = canvasRef.current;
      if (canvas && viewLogos[activeView]?.image) {
        const mockupBase64 = canvas.toDataURL('image/png').split(',')[1];
        savedMockups[activeView] = mockupBase64;
      }
      
      // Generate and save mockup for each view that has a logo
      for (const [view, logoData] of Object.entries(viewLogos)) {
        if (logoData.image && view !== activeView) {
          // Switch to view and capture canvas
          setActiveView(view);
          await new Promise(resolve => setTimeout(resolve, 200)); // Wait for canvas to update
          
          const mockupBase64 = canvas.toDataURL('image/png').split(',')[1];
          savedMockups[view] = mockupBase64;
        }
      }

      // Restore original view
      setActiveView(currentView);

      // Save all mockups
      await axios.put(`/proposals/${id}/mockup-views`, savedMockups);
      
      // Update local state
      setProposal(prev => ({ ...prev, mockup_views: savedMockups }));

      toast.success('All mockups saved successfully!');
    } catch (error) {
      console.error('Save all error:', error);
      toast.error('Failed to save all mockups');
    }
  };

  const resetLogoPosition = () => {
    setViewLogos(prev => ({
      ...prev,
      [activeView]: {
        ...prev[activeView],
        position: activeView.includes('sleeve') ? { x: 150, y: 250 } : { x: 150, y: 200 },
        size: activeView.includes('sleeve') ? 100 : 150
      }
    }));
  };
  
  const handleLogoSizeChange = (newSize) => {
    setViewLogos(prev => ({
      ...prev,
      [activeView]: {
        ...prev[activeView],
        size: parseInt(newSize)
      }
    }));
  };
  
  const removeCurrentLogo = () => {
    setViewLogos(prev => ({
      ...prev,
      [activeView]: {
        ...prev[activeView],
        image: null,
        file: null
      }
    }));
    toast.success(`Logo removed from ${activeView.replace('_', ' ').toUpperCase()}`);
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  return (
    <div className="dashboard" data-testid="mockup-designer">
      <AppHeader />

      <main className="dashboard-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
          <Button 
            variant="outline" 
            onClick={() => navigate(`/proposal/${id}`)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            ← Back to Proposal
          </Button>
          <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0 }}>Mockup Designer</h1>
          {proposal && (
            <span style={{ color: '#6b7280', fontSize: '14px' }}>
              {proposal.proposal_number} - {proposal.customer_name}
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '24px' }}>
          {/* Controls Panel */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Controls</h3>

            <div style={{ marginBottom: '24px' }}>
              <Label htmlFor="logo-upload">Upload Logo for {activeView.replace('_', ' ').toUpperCase()}</Label>
              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                style={{ 
                  marginTop: '8px',
                  padding: '10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  width: '100%',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                data-testid="logo-upload-input"
                key={activeView}
              />
              <p style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                Upload a logo and drag it to position on the shirt
              </p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <Label>View Angle</Label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginTop: '8px' }}>
                {['front', 'back', 'left_sleeve', 'right_sleeve'].map((view) => (
                  <Button
                    key={view}
                    variant={activeView === view ? 'default' : 'outline'}
                    onClick={() => setActiveView(view)}
                    data-testid={`view-${view}`}
                  >
                    {view.replace('_', ' ').toUpperCase()}
                  </Button>
                ))}
              </div>
            </div>

            {viewLogos[activeView]?.image && (
              <>
                <div style={{ marginBottom: '24px' }}>
                  <Label htmlFor="logo-size">Logo Size: {viewLogos[activeView].size}px</Label>
                  <input
                    id="logo-size"
                    type="range"
                    min="50"
                    max="300"
                    value={viewLogos[activeView].size}
                    onChange={(e) => handleLogoSizeChange(e.target.value)}
                    style={{ width: '100%', marginTop: '8px' }}
                    data-testid="logo-size-slider"
                  />
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Button onClick={resetLogoPosition} variant="outline" size="sm" data-testid="reset-button">
                    <RotateCcw size={16} /> Reset
                  </Button>
                  <Button onClick={removeCurrentLogo} variant="outline" size="sm" data-testid="remove-logo-button">
                    Remove Logo
                  </Button>
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
              <Button onClick={handleSaveMockup} data-testid="save-mockup-button">
                <Save size={16} /> Save {activeView.replace('_', ' ').toUpperCase()}
              </Button>
              <Button onClick={handleSaveAll} variant="outline" data-testid="save-all-button">
                <Save size={16} /> Save All
              </Button>
              <Button 
                onClick={async () => {
                  await handleSaveAll();
                  navigate(`/proposal/${id}`);
                }} 
                style={{ backgroundColor: '#22c55e' }}
                data-testid="save-return-button"
              >
                <Save size={16} /> Save & Return to Proposal
              </Button>
            </div>

            <div style={{ marginTop: '24px', padding: '16px', background: '#f7fafc', borderRadius: '8px' }}>
              <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#4a5568' }}>
                Logos Uploaded:
              </p>
              <div style={{ fontSize: '12px', color: '#718096' }}>
                {Object.entries(viewLogos).map(([view, data]) => (
                  <div key={view} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ 
                      display: 'inline-block', 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '50%', 
                      background: data.image ? '#22c55e' : '#e5e7eb' 
                    }}></span>
                    <span style={{ textTransform: 'capitalize' }}>{view.replace('_', ' ')}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop: '16px', padding: '16px', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fbbf24' }}>
              <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#92400e' }}>
                💡 Quick Tips:
              </p>
              <ul style={{ fontSize: '12px', color: '#78350f', paddingLeft: '20px', margin: 0 }}>
                <li>Upload different logos for each view</li>
                <li>Drag logos to position them perfectly</li>
                <li>Adjust size with the slider</li>
                <li>Save each view individually or use "Save All"</li>
                <li>Green dot = logo uploaded for that view</li>
              </ul>
            </div>
          </div>

          {/* Canvas Panel */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Preview</h3>
            <div style={{ textAlign: 'center' }}>
              <canvas
                ref={canvasRef}
                width={400}
                height={500}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: isDragging ? 'grabbing' : 'grab',
                  maxWidth: '100%'
                }}
                data-testid="mockup-canvas"
              />
              <p style={{ marginTop: '12px', fontSize: '12px', color: '#718096' }}>
                {viewLogos[activeView]?.image 
                  ? 'Click and drag logo to reposition' 
                  : `Upload a logo for ${activeView.replace('_', ' ').toUpperCase()}`
                }
              </p>
              <p style={{ marginTop: '4px', fontSize: '11px', color: '#9ca3af' }}>
                Current View: {activeView.replace('_', ' ').toUpperCase()}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MockupDesigner;
